export type PlatformEnvironment = 'local' | 'development' | 'staging' | 'production' | string;

export type FeatureFlagMap = Record<string, boolean>;

export interface PlatformConfig {
  apiBaseUrl: string;
  assetsBaseUrl: string;
  authBaseUrl: string;
  environment: PlatformEnvironment;
  releaseSha?: string;
  appVersion?: string;
  isEmbedded: boolean;
  embedHost?: string;
  featureFlags: FeatureFlagMap;
  telemetry?: {
    writeKey?: string;
    collectorUrl?: string;
  };
  extra?: Record<string, unknown>;
}

export type PlatformConfigInput = Partial<Omit<PlatformConfig, 'featureFlags'>> & {
  featureFlags?: Partial<FeatureFlagMap>;
};

type RuntimeGlobal = typeof globalThis & {
  __frontendPlatformRuntimeConfig__?: PlatformConfig;
};

const GLOBAL_CONFIG_KEY = '__frontendPlatformRuntimeConfig__';

let cachedConfig: PlatformConfig | undefined;
const subscribers = new Set<(config: PlatformConfig) => void>();

const runtimeGlobal = globalThis as RuntimeGlobal;

type ImportMetaEnv = {
  env?: Record<string, string>;
};

function readEnvValue(key: string): string | undefined {
  if (typeof process !== 'undefined' && process.env?.[key]) {
    return process.env[key];
  }

  if (typeof import.meta !== 'undefined') {
    const meta = import.meta as unknown as ImportMetaEnv;
    if (meta.env?.[key]) {
      return meta.env[key];
    }
  }

  if (typeof window !== 'undefined') {
    const dataValue = window.document.body?.dataset?.[key.toLowerCase() as never];
    if (typeof dataValue === 'string') {
      return dataValue;
    }
  }

  return undefined;
}

function detectEmbedding(): { isEmbedded: boolean; embedHost?: string } {
  if (typeof window === 'undefined') {
    return { isEmbedded: false };
  }

  const isIframe = window.self !== window.top;
  const hostAttribute =
    window.frameElement?.getAttribute('data-frontend-host') ??
    window.document.body?.dataset?.frontendHost ??
    undefined;

  const embedHost = hostAttribute ?? window.document.referrer ?? undefined;

  return {
    isEmbedded: isIframe || Boolean(hostAttribute),
    embedHost,
  };
}

function resolveBaseConfig(): PlatformConfig {
  const environment = (readEnvValue('VITE_ENVIRONMENT') ??
    readEnvValue('NX_ENV') ??
    'development') as PlatformEnvironment;

  const apiBaseUrl =
    readEnvValue('VITE_API_BASE_URL') ??
    (typeof window !== 'undefined' ? window.location.origin : '') ??
    '';

  const authBaseUrl = readEnvValue('VITE_AUTH_BASE_URL') ?? readEnvValue('NX_AUTH_BASE_URL') ?? '';

  const assetsBaseUrl =
    readEnvValue('VITE_ASSETS_BASE_URL') ??
    readEnvValue('NX_ASSETS_BASE_URL') ??
    (typeof window !== 'undefined' ? window.location.origin : '') ??
    '';

  const releaseSha = readEnvValue('VITE_RELEASE_SHA') ?? readEnvValue('NX_RELEASE_SHA');
  const appVersion = readEnvValue('VITE_APP_VERSION') ?? readEnvValue('NX_APP_VERSION');

  const { isEmbedded, embedHost } = detectEmbedding();

  return {
    apiBaseUrl,
    authBaseUrl: authBaseUrl || apiBaseUrl,
    assetsBaseUrl,
    environment,
    releaseSha,
    appVersion,
    isEmbedded,
    embedHost,
    featureFlags: {},
    telemetry: {
      writeKey: readEnvValue('VITE_ANALYTICS_WRITE_KEY'),
      collectorUrl: readEnvValue('VITE_ANALYTICS_COLLECTOR_URL'),
    },
    extra: {},
  };
}

function sanitizeFlags(flags?: Partial<FeatureFlagMap>): FeatureFlagMap {
  if (!flags) {
    return {};
  }

  return Object.entries(flags).reduce<FeatureFlagMap>((acc, [key, value]) => {
    if (typeof value === 'boolean') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function mergeConfigs(base: PlatformConfig, overrides?: PlatformConfigInput): PlatformConfig {
  if (!overrides) {
    return base;
  }

  return {
    ...base,
    ...overrides,
    featureFlags: {
      ...base.featureFlags,
      ...sanitizeFlags(overrides.featureFlags),
    },
    telemetry: {
      ...base.telemetry,
      ...(overrides.telemetry ?? {}),
    },
    extra: {
      ...base.extra,
      ...(overrides.extra ?? {}),
    },
  };
}

function loadFromGlobal(): PlatformConfig | undefined {
  return runtimeGlobal[GLOBAL_CONFIG_KEY];
}

function persistConfig(config: PlatformConfig) {
  runtimeGlobal[GLOBAL_CONFIG_KEY] = config;
}

export function definePlatformConfig(overrides?: PlatformConfigInput): PlatformConfig {
  const envConfig = resolveBaseConfig();
  return mergeConfigs(envConfig, overrides);
}

export function getRuntimeConfig(): PlatformConfig {
  if (cachedConfig) {
    return cachedConfig;
  }

  cachedConfig = loadFromGlobal() ?? resolveBaseConfig();
  persistConfig(cachedConfig);
  return cachedConfig;
}

export function setRuntimeConfig(overrides: PlatformConfigInput): PlatformConfig {
  cachedConfig = mergeConfigs(getRuntimeConfig(), overrides);
  persistConfig(cachedConfig);
  subscribers.forEach((listener) => {
    listener(cachedConfig as PlatformConfig);
  });
  return cachedConfig;
}

export function subscribeToRuntimeConfig(listener: (config: PlatformConfig) => void): () => void {
  subscribers.add(listener);
  if (cachedConfig) {
    listener(cachedConfig);
  }

  return () => {
    subscribers.delete(listener);
  };
}

export function getFeatureFlag(flag: string, fallback = false): boolean {
  const flags = getRuntimeConfig().featureFlags;
  if (flag in flags) {
    return Boolean(flags[flag]);
  }
  return fallback;
}

export function updateFeatureFlags(flags: Partial<FeatureFlagMap>): FeatureFlagMap {
  const next = {
    ...getRuntimeConfig().featureFlags,
    ...sanitizeFlags(flags),
  };
  setRuntimeConfig({ featureFlags: next });
  return next;
}

export function resetRuntimeConfig(): void {
  cachedConfig = resolveBaseConfig();
  persistConfig(cachedConfig);
  subscribers.forEach((listener) => {
    listener(cachedConfig as PlatformConfig);
  });
}

export function withRuntimeConfig<T>(fn: (config: PlatformConfig) => T): T {
  return fn(getRuntimeConfig());
}

export function isProduction(): boolean {
  const env = getRuntimeConfig().environment;
  return env === 'production';
}

export function isPreview(): boolean {
  const env = getRuntimeConfig().environment;
  return env === 'staging' || env === 'preview';
}
